/**
 * Admin Page
 * Admin dashboard for managing templates, users, and system metrics.
 */

import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChartBarIcon,
  UsersIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  SparklesIcon,
  TrashIcon as TrashOutlineIcon,
  Cog6ToothIcon,
  PlusIcon,
  EnvelopeIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline'
import {
  adminAPI,
  UserMetrics,
  AIRegistryResponse,
  CreateAIProviderPayload,
  CreateAIModelPayload,
  AccessLogsResponse,
  AdminConfigEntry,
  AssignCreditsRequest,
  BulkEmailRequest,
} from '@api/admin'
import toast from 'react-hot-toast'
import { logError } from '@utils/logger'
import { motion } from 'framer-motion'
import clsx from 'clsx'

type Tab = 'dashboard' | 'users' | 'templates' | 'tokens' | 'ai' | 'access-logs' | 'config'

const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard')
  const [uploadTemplateName, setUploadTemplateName] = useState('')
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)

  // AI registry form state
  const [newProviderForm, setNewProviderForm] = useState<CreateAIProviderPayload>({
    provider_key: '',
    display_name: '',
    provider_type: 'openai',
    api_key: '',
    default_model: '',
    is_enabled: true,
  })

  const [newModelForm, setNewModelForm] = useState<CreateAIModelPayload>({
    provider_key: '',
    model_id: '',
    display_name: '',
    max_tokens: undefined,
    temperature: undefined,
    is_enabled: true,
  })

  const queryClient = useQueryClient()
  
  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => adminAPI.deleteUser(userId),
    onSuccess: () => {
      setShowDeleteConfirm(false)
      setUserToDelete(null)
      setSelectedUserForAction(null)
      // Refetch user metrics
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-metrics'] })
    },
    onError: (error: any) => {
      logError('Failed to delete user', error)
      const message =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to delete user. Please try again.'
    },
  })
  
  const [selectedUserForAction, setSelectedUserForAction] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [userToDelete, setUserToDelete] = useState<UserMetrics | null>(null)

  // Bulk email state (admin -> users)
  const [selectedUserEmails, setSelectedUserEmails] = useState<string[]>([])
  const [bulkEmailSubject, setBulkEmailSubject] = useState('')
  const [bulkEmailBody, setBulkEmailBody] = useState('')
  const [bulkEmailFromAddress, setBulkEmailFromAddress] = useState('info@scidraft.com')
  const [isLoadingAllEmails, setIsLoadingAllEmails] = useState(false)
  
  // Available email aliases
  const emailAliases = [
    { value: 'info@scidraft.com', label: 'Info (info@scidraft.com)' },
    { value: 'elo@scidraft.com', label: 'Elo (elo@scidraft.com)' },
    { value: 'support@scidraft.com', label: 'Support (support@scidraft.com)' },
    { value: 'no-reply@scidraft.com', label: 'No Reply (no-reply@scidraft.com)' },
  ]

  const bulkEmailMutation = useMutation({
    mutationFn: (payload: BulkEmailRequest) => adminAPI.sendBulkEmail(payload),
    onSuccess: (result) => {
      setBulkEmailSubject('')
      setBulkEmailBody('')
      setSelectedUserEmails([])
    },
    onError: (error: any) => {
      logError('Failed to send bulk email', error)
      const message =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to send bulk email. Please try again.'
    },
  })

  // System metrics
  const { data: systemMetrics, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ['admin', 'system-metrics'],
    queryFn: () => adminAPI.getSystemMetrics(),
    refetchInterval: 30000, // Refetch every 30 seconds
  })

  // User metrics
  const { data: userMetrics, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['admin', 'user-metrics'],
    queryFn: () => adminAPI.getUserMetrics(0, 1000),
  })

  // Templates
  const { data: templates, isLoading: isLoadingTemplates, refetch: refetchTemplates } = useQuery({
    queryKey: ['admin', 'templates'],
    queryFn: () => adminAPI.listTemplates(),
  })

  // Token usage
  const { data: tokenUsage, isLoading: isLoadingTokens } = useQuery({
    queryKey: ['admin', 'token-usage'],
    queryFn: () => adminAPI.getSystemTokenUsage(),
    refetchInterval: 30000, // Refetch every 30 seconds
  })

  // Runtime metrics
  const { data: runtimeMetrics, isLoading: isLoadingRuntime } = useQuery({
    queryKey: ['admin', 'runtime-metrics'],
    queryFn: () => adminAPI.getRuntimeMetrics(),
    refetchInterval: 15000, // Refetch every 15 seconds
  })

  const { data: topProcesses, isLoading: isLoadingTopProcesses } = useQuery({
    queryKey: ['admin', 'top-processes'],
    queryFn: () => adminAPI.getTopProcesses(10),
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  // AI registry
  const {
    data: aiRegistry,
    isLoading: isLoadingAIRegistry,
    refetch: refetchAIRegistry,
  } = useQuery<AIRegistryResponse>({
    queryKey: ['admin', 'ai-registry'],
    queryFn: () => adminAPI.getAIRegistry(),
  })

  // Access logs
  const [accessLogsPage, setAccessLogsPage] = useState(0)
  const [accessLogsFilter, setAccessLogsFilter] = useState({
    ip_address: '',
    endpoint: '',
  })
  const { data: accessLogs, isLoading: isLoadingAccessLogs, refetch: refetchAccessLogs } = useQuery<AccessLogsResponse>({
    queryKey: ['admin', 'access-logs', accessLogsPage, accessLogsFilter],
    queryFn: () => adminAPI.getAccessLogs({
      limit: 50,
      offset: accessLogsPage * 50,
      ...(accessLogsFilter.ip_address && { ip_address: accessLogsFilter.ip_address }),
      ...(accessLogsFilter.endpoint && { endpoint: accessLogsFilter.endpoint }),
    }),
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  // Admin config
  const { data: adminConfig, isLoading: isLoadingConfig, refetch: refetchConfig } = useQuery<AdminConfigEntry[]>({
    queryKey: ['admin', 'config'],
    queryFn: () => adminAPI.getAllConfig(),
  })

  // Assign credits form state
  const [assignCreditsForm, setAssignCreditsForm] = useState<AssignCreditsRequest>({
    user_email: '',  // Changed from user_id to user_email
    credits: 0,
    description: '',
  })
  const [userSearchResults, setUserSearchResults] = useState<any[]>([])
  const [showUserSearchResults, setShowUserSearchResults] = useState(false)
  const [searchingUsers, setSearchingUsers] = useState(false)

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: (templateName: string) => adminAPI.deleteTemplate(templateName),
    onSuccess: () => {
      refetchTemplates()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete template'
      logError('Failed to delete template:', error)
    },
  })

  // Upload template mutation
  const uploadTemplateMutation = useMutation({
    mutationFn: ({ name, file }: { name: string; file: File }) => adminAPI.uploadTemplate(name, file),
    onSuccess: () => {
      setShowUploadModal(false)
      setUploadTemplateName('')
      setUploadFile(null)
      refetchTemplates()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to upload template'
      logError('Failed to upload template:', error)
    },
  })

  // Create/update AI provider
  const createProviderMutation = useMutation({
    mutationFn: (payload: CreateAIProviderPayload) => adminAPI.createOrUpdateAIProvider(payload),
    onSuccess: () => {
      refetchAIRegistry()
      setNewProviderForm({
        provider_key: '',
        display_name: '',
        provider_type: 'openai',
        api_key: '',
        default_model: '',
        is_enabled: true,
      })
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.detail || error.message || 'Failed to save provider'
      logError('Failed to save AI provider:', error)
    },
  })

  // Add/update AI model
  const addModelMutation = useMutation({
    mutationFn: (payload: CreateAIModelPayload) => adminAPI.addAIModel(payload),
    onSuccess: () => {
      refetchAIRegistry()
      setNewModelForm({
        provider_key: '',
        model_id: '',
        display_name: '',
        max_tokens: undefined,
        temperature: undefined,
        is_enabled: true,
      })
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.detail || error.message || 'Failed to save model'
      logError('Failed to save AI model:', error)
    },
  })

  // Delete AI model
  const deleteModelMutation = useMutation({
    mutationFn: (params: { providerKey: string; modelId: string }) =>
      adminAPI.deleteAIModel(params.providerKey, params.modelId),
    onSuccess: () => {
      refetchAIRegistry()
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.detail || error.message || 'Failed to delete model'
      logError('Failed to delete AI model:', error)
    },
  })

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: any }) => adminAPI.updateConfig(key, value),
    onSuccess: () => {
      refetchConfig()
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.detail || error.message || 'Failed to update configuration'
      logError('Failed to update config:', error)
    },
  })

  // Assign credits mutation
  const assignCreditsMutation = useMutation({
    mutationFn: (request: AssignCreditsRequest) => adminAPI.assignCredits(request),
    onSuccess: (data) => {
      setAssignCreditsForm({ user_email: '', credits: 0, description: '' })
      setUserSearchResults([])
      setShowUserSearchResults(false)
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.detail || error.message || 'Failed to assign credits'
      logError('Failed to assign credits:', error)
    },
  })

  // User search handler
  const handleUserSearch = async (email: string) => {
    if (email.length < 2) {
      setUserSearchResults([])
      setShowUserSearchResults(false)
      return
    }
    
    setSearchingUsers(true)
    try {
      const result = await adminAPI.searchUsersByEmail(email)
      setUserSearchResults(result.users)
      setShowUserSearchResults(true)
    } catch (error) {
      logError('Failed to search users:', error)
    } finally {
      setSearchingUsers(false)
    }
  }

  const handleUpload = () => {
    if (!uploadTemplateName.trim()) {
      return
    }
    if (!uploadFile) {
      return
    }
    uploadTemplateMutation.mutate({ name: uploadTemplateName.trim(), file: uploadFile })
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return 'N/A'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const formatPercent = (value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) return 'N/A'
    return `${value.toFixed(1)}%`
  }

  const formatMegabytes = (value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) return 'N/A'
    return `${value.toFixed(1)} MB`
  }

  const formatUptime = (seconds?: number) => {
    if (!seconds || Number.isNaN(seconds)) return 'N/A'
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  const getUsageColor = (value: number) => {
    if (value >= 85) return 'bg-rose-500'
    if (value >= 70) return 'bg-amber-400'
    return 'bg-teal-500'
  }

  const UsageBar = ({ value }: { value?: number | null }) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-slate-700" />
    }
    return (
      <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-slate-700">
        <div
          className={clsx('h-2 rounded-full transition-all', getUsageColor(value))}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
    )
  }

  const StatusDot = ({ healthy }: { healthy: boolean }) => (
    <span
      className={clsx(
        'inline-flex h-2.5 w-2.5 rounded-full',
        healthy ? 'bg-teal-500' : 'bg-amber-400'
      )}
    />
  )

  // ConfigRow component for editing config values
  const ConfigRow: React.FC<{
    config: AdminConfigEntry
    onUpdate: (value: any) => void
    isUpdating: boolean
  }> = ({ config, onUpdate, isUpdating }: {
    config: AdminConfigEntry
    onUpdate: (value: any) => void
    isUpdating: boolean
  }) => {
    const [editValue, setEditValue] = useState<any>(config.config_value)
    const [isEditing, setIsEditing] = useState(false)

    const handleSave = () => {
      // Convert value based on type
      let convertedValue: any = editValue
      if (config.config_type === 'integer') {
        convertedValue = parseInt(editValue)
        if (isNaN(convertedValue)) {
          return
        }
      } else if (config.config_type === 'float') {
        convertedValue = parseFloat(editValue)
        if (isNaN(convertedValue)) {
          return
        }
      } else if (config.config_type === 'boolean') {
        convertedValue = editValue === true || editValue === 'true' || editValue === '1'
      }
      onUpdate(convertedValue)
      setIsEditing(false)
    }

    const handleCancel = () => {
      setEditValue(config.config_value)
      setIsEditing(false)
    }

    return (
      <div className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-slate-900 dark:text-white">{config.config_key}</span>
              <span className="text-xs px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                {config.config_type}
              </span>
            </div>
            {config.description && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">{config.description}</p>
            )}
            {isEditing ? (
              <div className="flex items-center gap-2 mt-2">
                {config.config_type === 'boolean' ? (
                  <select
                    value={editValue ? 'true' : 'false'}
                    onChange={(e) => setEditValue(e.target.value === 'true')}
                    className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                ) : (
                  <input
                    type={config.config_type === 'integer' || config.config_type === 'float' ? 'number' : 'text'}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                )}
                <button
                  onClick={handleSave}
                  disabled={isUpdating}
                  className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isUpdating}
                  className="px-3 py-1.5 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-slate-900 dark:text-white font-mono text-sm">
                  {config.config_type === 'boolean' ? String(config.config_value) : String(config.config_value)}
                </span>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-2 py-1 text-xs bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                >
                  Edit
                </button>
              </div>
            )}
            {config.updated_at && (
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                Last updated: {new Date(config.updated_at).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'dashboard' as Tab, name: 'Dashboard', icon: ChartBarIcon },
    { id: 'users' as Tab, name: 'Users & Email', icon: UsersIcon },
    { id: 'templates' as Tab, name: 'Templates', icon: DocumentTextIcon },
    { id: 'tokens' as Tab, name: 'Credit Usage', icon: CurrencyDollarIcon },
    { id: 'ai' as Tab, name: 'AI Providers', icon: SparklesIcon },
    { id: 'access-logs' as Tab, name: 'Access Logs', icon: ChartBarIcon },
    { id: 'config' as Tab, name: 'Configuration', icon: Cog6ToothIcon },
  ]

  return (
    <div className="h-full w-full overflow-y-auto bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
            Admin Dashboard
          </h1>
          <p className="mt-2 text-slate-600 dark:text-slate-400">
            Manage templates, users, AI providers, and monitor system metrics
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-slate-200 dark:border-slate-700">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'flex items-center px-1 py-4 border-b-2 font-medium text-sm transition-colors',
                    activeTab === tab.id
                      ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
                  )}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {isLoadingMetrics ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                <p className="mt-4 text-slate-600 dark:text-slate-400">Loading metrics...</p>
              </div>
            ) : systemMetrics ? (
              <>
                {/* Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Users</p>
                        <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                          {formatNumber(systemMetrics.total_users)}
                        </p>
                      </div>
                      <UsersIcon className="w-12 h-12 text-teal-500" />
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Documents</p>
                        <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                          {formatNumber(systemMetrics.total_documents)}
                        </p>
                      </div>
                      <DocumentTextIcon className="w-12 h-12 text-cyan-500" />
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Token Usage</p>
                        <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                          {formatNumber(systemMetrics.total_token_usage)}
                        </p>
                      </div>
                      <CurrencyDollarIcon className="w-12 h-12 text-teal-500" />
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Cost</p>
                        <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                          {formatCurrency(systemMetrics.total_cost_usd)}
                        </p>
                      </div>
                      <CurrencyDollarIcon className="w-12 h-12 text-cyan-500" />
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Active Users (30d)</p>
                        <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                          {formatNumber(systemMetrics.active_users_last_30_days)}
                        </p>
                      </div>
                      <UsersIcon className="w-12 h-12 text-teal-500" />
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Documents Created (30d)</p>
                        <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                          {formatNumber(systemMetrics.documents_created_last_30_days)}
                        </p>
                      </div>
                      <DocumentTextIcon className="w-12 h-12 text-cyan-500" />
                    </div>
                  </motion.div>
                </div>

                {/* Runtime Metrics */}
                <section className="space-y-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                      Runtime Metrics
                    </h2>
                    <div className="flex items-center gap-2">
                    <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {runtimeMetrics
                        ? `Updated ${new Date(runtimeMetrics.timestamp).toLocaleTimeString()}`
                        : 'Awaiting data'}
                    </p>
                    </div>
                  </div>

                  {isLoadingRuntime ? (
                    <div className="text-center py-10">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                      <p className="mt-4 text-slate-600 dark:text-slate-400">Collecting runtime metrics...</p>
                    </div>
                  ) : runtimeMetrics ? (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                      <div className="space-y-6">
                        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                                Process Health
                              </h3>
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                Uptime {formatUptime(runtimeMetrics.process.uptime_seconds)}
                              </p>
                            </div>
                          </div>
                          <div className="mt-4 space-y-4">
                            <div>
                              <div className="flex items-center justify-between text-sm text-slate-600 dark:text-slate-300">
                                <span>CPU</span>
                                <span className="font-medium">
                                  {formatPercent(runtimeMetrics.process.cpu_percent)}
                                </span>
                              </div>
                              <UsageBar value={runtimeMetrics.process.cpu_percent} />
                            </div>
                            <div>
                              <div className="flex items-center justify-between text-sm text-slate-600 dark:text-slate-300">
                                <span>Memory</span>
                                <span className="font-medium">
                                  {formatPercent(runtimeMetrics.process.memory_percent)} ·{' '}
                                  {formatMegabytes(runtimeMetrics.process.memory_mb)}
                                </span>
                              </div>
                              <UsageBar value={runtimeMetrics.process.memory_percent} />
                            </div>
                            <div className="text-sm text-slate-600 dark:text-slate-400">
                              Threads: {runtimeMetrics.process.threads}
                            </div>
                          </div>
                        </div>

                        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">System Load</h3>
                          <div className="mt-4 space-y-4">
                            <div>
                              <div className="flex items-center justify-between text-sm text-slate-600 dark:text-slate-300">
                                <span>Total CPU</span>
                                <span className="font-medium">
                                  {formatPercent(runtimeMetrics.system.cpu_percent)}
                                </span>
                              </div>
                              <UsageBar value={runtimeMetrics.system.cpu_percent} />
                            </div>
                            <div>
                              <div className="flex items-center justify-between text-sm text-slate-600 dark:text-slate-300">
                                <span>Memory</span>
                                <span className="font-medium">
                                  {formatPercent(runtimeMetrics.system.memory_percent)} ·{' '}
                                  {formatMegabytes(runtimeMetrics.system.memory_used_mb)} /{' '}
                                  {formatMegabytes(runtimeMetrics.system.memory_total_mb)}
                                </span>
                              </div>
                              <UsageBar value={runtimeMetrics.system.memory_percent} />
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-sm text-slate-600 dark:text-slate-400">
                              <div>
                                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                  Load 1m
                                </p>
                                <p className="font-semibold text-slate-900 dark:text-white">
                                  {runtimeMetrics.system.load_average_1m.toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                  Load 5m
                                </p>
                                <p className="font-semibold text-slate-900 dark:text-white">
                                  {runtimeMetrics.system.load_average_5m.toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                  Load 15m
                                </p>
                                <p className="font-semibold text-slate-900 dark:text-white">
                                  {runtimeMetrics.system.load_average_15m.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-6">
                        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Database Pool</h3>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              Target {runtimeMetrics.database.max_size} connections
                            </span>
                          </div>
                          <div className="mt-4 space-y-3 text-sm text-slate-600 dark:text-slate-300">
                            <div className="flex items-center justify-between">
                              <span>In use</span>
                              <span className="font-medium">
                                {runtimeMetrics.database.used_connections}/{runtimeMetrics.database.max_size}
                              </span>
                            </div>
                            <UsageBar
                              value={
                                runtimeMetrics.database.max_size
                                  ? (runtimeMetrics.database.used_connections /
                                      runtimeMetrics.database.max_size) *
                                    100
                                  : undefined
                              }
                            />
                            <div className="flex items-center justify-between">
                              <span>Idle</span>
                              <span>{runtimeMetrics.database.idle_connections}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Pool size</span>
                              <span>{runtimeMetrics.database.pool_size}</span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Redis</h3>
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                              <StatusDot healthy={!!runtimeMetrics.redis.connected} />
                              {runtimeMetrics.redis.connected ? 'Online' : 'Offline'}
                            </div>
                          </div>
                          <div className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                            <div className="flex items-center justify-between">
                              <span>Memory</span>
                              <span>{formatMegabytes(runtimeMetrics.redis.used_memory_mb)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Clients</span>
                              <span>{runtimeMetrics.redis.connected_clients ?? 'N/A'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Ops / sec</span>
                              <span>{runtimeMetrics.redis.ops_per_sec ?? 'N/A'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Keys</span>
                              <span>{runtimeMetrics.redis.key_count ?? 'N/A'}</span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Celery Workers</h3>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              Target concurrency {runtimeMetrics.celery.configured_concurrency ?? '—'}
                            </span>
                          </div>
                          <div className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                            <div className="flex items-center justify-between">
                              <span>Queue depth</span>
                              <span>{runtimeMetrics.celery.queue_depth ?? 'N/A'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Scheduled</span>
                              <span>{runtimeMetrics.celery.scheduled_tasks ?? 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Top Processes by Memory */}
                      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                            Top Processes by Memory
                          </h3>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {topProcesses ? `${topProcesses.length} processes` : 'Loading...'}
                          </span>
                        </div>
                        {isLoadingTopProcesses ? (
                          <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                          </div>
                        ) : topProcesses && topProcesses.length > 0 ? (
                          <div className="space-y-2">
                            {topProcesses.map((proc, idx) => (
                              <div
                                key={proc.pid}
                                className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
                                      #{idx + 1}
                                    </span>
                                    <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                      {proc.name}
                                    </span>
                                    <span className="text-xs text-slate-500 dark:text-slate-400">
                                      (PID: {proc.pid})
                                    </span>
                                  </div>
                                  <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                                    {proc.username}
                                  </div>
                                </div>
                                <div className="ml-4 text-right">
                                  <div className="text-sm font-semibold text-slate-900 dark:text-white">
                                    {formatMegabytes(proc.memory_mb)}
                                  </div>
                                  <div className="text-xs text-slate-500 dark:text-slate-400">
                                    {formatPercent(proc.memory_percent)} of system
                                  </div>
                                  {proc.cpu_percent > 0 && (
                                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                      CPU: {formatPercent(proc.cpu_percent)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-slate-600 dark:text-slate-400 text-sm">
                            No process data available
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-10 text-slate-600 dark:text-slate-400">
                      Unable to load runtime metrics
                    </div>
                  )}
                </section>
              </>
            ) : (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                Failed to load metrics
              </div>
            )}
          </div>
        )}

        {/* AI Providers Tab */}
        {activeTab === 'ai' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Providers + models list */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <SparklesIcon className="w-5 h-5 text-teal-500" />
                AI Providers & Models
              </h2>
              {isLoadingAIRegistry ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
                  <p className="mt-3 text-slate-600 dark:text-slate-400">
                    Loading AI providers and models...
                  </p>
                </div>
              ) : aiRegistry && aiRegistry.providers.length > 0 ? (
                <div className="space-y-4">
                  {aiRegistry.providers.map((provider) => (
                    <div
                      key={provider.id}
                      className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-white">
                            {provider.display_name}{' '}
                            <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                              ({provider.provider_key})
                            </span>
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            Type: {provider.provider_type} · Default model:{' '}
                            <span className="font-mono">
                              {provider.default_model || 'not set'}
                            </span>
                          </p>
                        </div>
                        <StatusDot healthy={provider.is_enabled} />
                      </div>

                      <div className="mt-4">
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                          Models
                        </p>
                        {provider.models.length === 0 ? (
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            No models configured yet.
                          </p>
                        ) : (
                          <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                            {provider.models.map((model) => (
                              <div
                                key={model.id}
                                className="flex items-center justify-between text-xs px-2 py-1 rounded-md bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-slate-900 dark:text-slate-100">
                                    {model.model_id}
                                  </span>
                                  <span className="ml-1 text-slate-500 dark:text-slate-400">
                                    ({model.display_name})
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span
                                    className={clsx(
                                      'px-2 py-0.5 rounded-full text-[10px] font-medium',
                                      model.is_enabled
                                        ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-200'
                                        : 'bg-slate-100 text-slate-600 dark:bg-slate-900/40 dark:text-slate-300'
                                    )}
                                  >
                                    {model.is_enabled ? 'Enabled' : 'Disabled'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (
                                        !window.confirm(
                                          `Remove model '${model.model_id}' from provider '${provider.provider_key}'?`
                                        )
                                      ) {
                                        return
                                      }
                                      deleteModelMutation.mutate({
                                        providerKey: provider.provider_key,
                                        modelId: model.model_id,
                                      })
                                    }}
                                    className="inline-flex items-center justify-center h-5 w-5 rounded-full hover:bg-red-50 dark:hover:bg-red-900/40 text-red-500"
                                    title="Delete model"
                                  >
                                    <TrashOutlineIcon className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  No AI providers configured yet. Use the forms on the right to add one.
                </p>
              )}
            </div>

            {/* Forms: add provider + model */}
            <div className="space-y-6">
              {/* Add / update provider */}
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">
                  Add / Update Provider
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                  Use this to configure a provider&apos;s API key and default model. Changes
                  take effect immediately for all users.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                      Provider Key
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      placeholder="e.g. openai, anthropic, grok"
                      value={newProviderForm.provider_key}
                      onChange={(e) =>
                        setNewProviderForm((prev) => ({
                          ...prev,
                          provider_key: e.target.value.trim(),
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                      Display Name
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      placeholder="e.g. OpenAI, Anthropic Claude"
                      value={newProviderForm.display_name}
                      onChange={(e) =>
                        setNewProviderForm((prev) => ({
                          ...prev,
                          display_name: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                      Provider Type
                    </label>
                    <select
                      className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      value={newProviderForm.provider_type}
                      onChange={(e) =>
                        setNewProviderForm((prev) => ({
                          ...prev,
                          provider_type: e.target.value,
                        }))
                      }
                    >
                      <option value="openai">OpenAI</option>
                      <option value="anthropic">Anthropic</option>
                      <option value="grok">Grok</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                      API Key
                    </label>
                    <input
                      type="password"
                      className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500 font-mono"
                      value={newProviderForm.api_key}
                      onChange={(e) =>
                        setNewProviderForm((prev) => ({
                          ...prev,
                          api_key: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                      Default Model (optional)
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500 font-mono"
                      placeholder="e.g. gpt-4o, claude-3-5-sonnet-20241022"
                      value={newProviderForm.default_model ?? ''}
                      onChange={(e) =>
                        setNewProviderForm((prev) => ({
                          ...prev,
                          default_model: e.target.value || undefined,
                        }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <label className="flex items-center text-xs text-slate-600 dark:text-slate-300">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
                        checked={newProviderForm.is_enabled ?? true}
                        onChange={(e) =>
                          setNewProviderForm((prev) => ({
                            ...prev,
                            is_enabled: e.target.checked,
                          }))
                        }
                      />
                      <span className="ml-2">Enabled</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (!newProviderForm.provider_key.trim()) {
                          return
                        }
                        if (!newProviderForm.display_name.trim()) {
                          return
                        }
                        if (!newProviderForm.api_key.trim()) {
                          return
                        }
                        createProviderMutation.mutate(newProviderForm)
                      }}
                      className="inline-flex items-center px-3 py-1.5 rounded-md bg-teal-600 text-white text-xs font-medium hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                    >
                      Save Provider
                    </button>
                  </div>
                </div>
              </div>

              {/* Add model */}
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">
                  Add / Update Model
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                  Attach models to providers. Users will only be able to select enabled
                  models.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                      Provider
                    </label>
                    <select
                      className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      value={newModelForm.provider_key}
                      onChange={(e) =>
                        setNewModelForm((prev) => ({
                          ...prev,
                          provider_key: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select provider…</option>
                      {aiRegistry?.providers.map((p) => (
                        <option key={p.provider_key} value={p.provider_key}>
                          {p.display_name} ({p.provider_key})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                      Model ID
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500 font-mono"
                      placeholder="e.g. gpt-4o, claude-3-5-sonnet-20241022"
                      value={newModelForm.model_id}
                      onChange={(e) =>
                        setNewModelForm((prev) => ({
                          ...prev,
                          model_id: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                      Display Name
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      placeholder="Friendly label for the model"
                      value={newModelForm.display_name}
                      onChange={(e) =>
                        setNewModelForm((prev) => ({
                          ...prev,
                          display_name: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                        Max Tokens (optional)
                      </label>
                      <input
                        type="number"
                        className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                        value={newModelForm.max_tokens ?? ''}
                        onChange={(e) =>
                          setNewModelForm((prev) => ({
                            ...prev,
                            max_tokens: e.target.value ? Number(e.target.value) : undefined,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                        Temperature (optional)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="2"
                        className="mt-1 block w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                        value={newModelForm.temperature ?? ''}
                        onChange={(e) =>
                          setNewModelForm((prev) => ({
                            ...prev,
                            temperature: e.target.value ? Number(e.target.value) : undefined,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <label className="flex items-center text-xs text-slate-600 dark:text-slate-300">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
                        checked={newModelForm.is_enabled ?? true}
                        onChange={(e) =>
                          setNewModelForm((prev) => ({
                            ...prev,
                            is_enabled: e.target.checked,
                          }))
                        }
                      />
                      <span className="ml-2">Enabled</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (!newModelForm.provider_key) {
                          return
                        }
                        if (!newModelForm.model_id.trim()) {
                          return
                        }
                        if (!newModelForm.display_name.trim()) {
                          return
                        }
                        addModelMutation.mutate(newModelForm)
                      }}
                      className="inline-flex items-center px-3 py-1.5 rounded-md bg-teal-600 text-white text-xs font-medium hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                    >
                      Save Model
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Access Logs Tab */}
        {activeTab === 'access-logs' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Requests</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                      {accessLogs?.total.toLocaleString() || 0}
                    </p>
                  </div>
                  <ChartBarIcon className="w-12 h-12 text-teal-500" />
                </div>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Unique IPs</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                      {accessLogs?.unique_ips.toLocaleString() || 0}
                    </p>
                  </div>
                  <UsersIcon className="w-12 h-12 text-blue-500" />
                </div>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Current Page</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                      {accessLogsPage + 1}
                    </p>
                  </div>
                  <DocumentTextIcon className="w-12 h-12 text-purple-500" />
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    IP Address
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    placeholder="Filter by IP address..."
                    value={accessLogsFilter.ip_address}
                    onChange={(e) =>
                      setAccessLogsFilter((prev) => ({ ...prev, ip_address: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Endpoint
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-md border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    placeholder="Filter by endpoint..."
                    value={accessLogsFilter.endpoint}
                    onChange={(e) =>
                      setAccessLogsFilter((prev) => ({ ...prev, endpoint: e.target.value }))
                    }
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  setAccessLogsFilter({ ip_address: '', endpoint: '' })
                  setAccessLogsPage(0)
                  refetchAccessLogs()
                }}
                className="mt-4 px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
              >
                Clear Filters
              </button>
            </div>

            {/* Access Logs Table */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              {isLoadingAccessLogs ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                  <p className="mt-4 text-slate-600 dark:text-slate-400">Loading access logs...</p>
                </div>
              ) : accessLogs && accessLogs.logs.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                      <thead className="bg-slate-50 dark:bg-slate-900">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            Timestamp
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            IP Address
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            Method
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            Endpoint
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            Response Time
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                        {accessLogs.logs.map((log) => (
                          <tr key={log.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-white">
                              {new Date(log.created_at).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-900 dark:text-white">
                              {log.ip_address}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-white">
                              {log.username || log.email || (
                                <span className="text-slate-400 italic">Anonymous</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-white">
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  log.method === 'GET'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : log.method === 'POST'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : log.method === 'PUT'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    : log.method === 'DELETE'
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    : 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200'
                                }`}
                              >
                                {log.method || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-900 dark:text-white">
                              <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                                {log.endpoint || 'N/A'}
                              </code>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  log.status_code && log.status_code >= 200 && log.status_code < 300
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : log.status_code && log.status_code >= 300 && log.status_code < 400
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    : log.status_code && log.status_code >= 400
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    : 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200'
                                }`}
                              >
                                {log.status_code || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-white">
                              {log.response_time_ms !== null && log.response_time_ms !== undefined
                                ? `${log.response_time_ms}ms`
                                : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  <div className="bg-slate-50 dark:bg-slate-900 px-6 py-4 flex items-center justify-between border-t border-slate-200 dark:border-slate-700">
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      Showing {accessLogsPage * 50 + 1} to{' '}
                      {Math.min((accessLogsPage + 1) * 50, accessLogs.total)} of {accessLogs.total} logs
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (accessLogsPage > 0) {
                            setAccessLogsPage(accessLogsPage - 1)
                          }
                        }}
                        disabled={accessLogsPage === 0}
                        className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          if (accessLogs && (accessLogsPage + 1) * 50 < accessLogs.total) {
                            setAccessLogsPage(accessLogsPage + 1)
                          }
                        }}
                        disabled={
                          !accessLogs || (accessLogsPage + 1) * 50 >= accessLogs.total
                        }
                        className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-slate-600 dark:text-slate-400">No access logs found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <div className="space-y-6">
            {/* Assign Credits Section */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <CurrencyDollarIcon className="w-5 h-5" />
                Assign Credits to User
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    User Email
                  </label>
                  <input
                    type="email"
                    value={assignCreditsForm.user_email}
                    onChange={(e) => {
                      setAssignCreditsForm((prev) => ({ ...prev, user_email: e.target.value }))
                      handleUserSearch(e.target.value)
                    }}
                    onFocus={() => {
                      if (userSearchResults.length > 0) {
                        setShowUserSearchResults(true)
                      }
                    }}
                    onBlur={() => {
                      // Delay hiding to allow clicking on results
                      setTimeout(() => setShowUserSearchResults(false), 200)
                    }}
                    placeholder="Search by email..."
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  {searchingUsers && (
                    <div className="absolute right-3 top-9">
                      <div className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {showUserSearchResults && userSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {userSearchResults.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => {
                            setAssignCreditsForm((prev) => ({ ...prev, user_email: user.email }))
                            setShowUserSearchResults(false)
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          <div className="text-sm font-medium text-slate-900 dark:text-white">{user.email}</div>
                          {user.username && (
                            <div className="text-xs text-slate-500 dark:text-slate-400">{user.username}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Credits
                  </label>
                  <input
                    type="number"
                    value={assignCreditsForm.credits}
                    onChange={(e) =>
                      setAssignCreditsForm((prev) => ({
                        ...prev,
                        credits: parseInt(e.target.value) || 0,
                      }))
                    }
                    placeholder="Enter credits amount"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Description (Optional)
                  </label>
                  <input
                    type="text"
                    value={assignCreditsForm.description}
                    onChange={(e) =>
                      setAssignCreditsForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Reason for assignment"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  if (!assignCreditsForm.user_email || assignCreditsForm.credits <= 0) {
                    return
                  }
                  assignCreditsMutation.mutate(assignCreditsForm)
                }}
                disabled={assignCreditsMutation.isPending}
                className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {assignCreditsMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-4 h-4" />
                    Assign Credits
                  </>
                )}
              </button>
            </div>

            {/* Configuration Values */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Cog6ToothIcon className="w-5 h-5" />
                  System Configuration
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Configure limits, credit costs, and other system settings
                </p>
              </div>
              {isLoadingConfig ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                  <p className="mt-4 text-slate-600 dark:text-slate-400">Loading configuration...</p>
                </div>
              ) : adminConfig && adminConfig.length > 0 ? (
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  {adminConfig.map((config) => (
                    <ConfigRow
                      key={config.config_key}
                      config={config}
                      onUpdate={(value: any) => {
                        updateConfigMutation.mutate({ key: config.config_key, value })
                      }}
                      isUpdating={updateConfigMutation.isPending}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-slate-600 dark:text-slate-400">No configuration found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Bulk Email Composer */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <EnvelopeIcon className="w-5 h-5" />
                    Bulk Email to Selected Users
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Select users from the table below, or send to all registered users.
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={async () => {
                      setIsLoadingAllEmails(true)
                      try {
                        const result = await adminAPI.getAllUserEmails()
                        setSelectedUserEmails(result.emails)
                      } catch (error: any) {
                        logError('Failed to load all user emails', error)
                      } finally {
                        setIsLoadingAllEmails(false)
                      }
                    }}
                    disabled={isLoadingAllEmails}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoadingAllEmails ? (
                      <>
                        <div className="w-3 h-3 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <UsersIcon className="w-4 h-4 mr-1.5" />
                        Load All Users
                      </>
                    )}
                  </button>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  {selectedUserEmails.length} recipient{selectedUserEmails.length === 1 ? '' : 's'} selected
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    From Address
                  </label>
                  <select
                    value={bulkEmailFromAddress}
                    onChange={(e) => setBulkEmailFromAddress(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {emailAliases.map((alias) => (
                      <option key={alias.value} value={alias.value}>
                        {alias.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Choose the email address recipients will see as the sender.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={bulkEmailSubject}
                    onChange={(e) => setBulkEmailSubject(e.target.value)}
                    placeholder="SciDraft is coming soon"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Email Content
                  </label>
                  <textarea
                    value={bulkEmailBody}
                    onChange={(e) => setBulkEmailBody(e.target.value)}
                    rows={8}
                    placeholder="Write your email content here. You can use plain text or HTML. The content will be automatically wrapped in a beautiful SciDraft-branded template."
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    ✨ Your content will be automatically wrapped in a modern SciDraft-branded email template with logo and footer.
                    <br />
                    You can write plain text (paragraphs separated by blank lines) or HTML. Line breaks are preserved.
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (!bulkEmailSubject.trim() || !bulkEmailBody.trim()) {
                      return
                    }
                    if (selectedUserEmails.length === 0) {
                      return
                    }
                    const payload: BulkEmailRequest = {
                      subject: bulkEmailSubject.trim(),
                      html_body: bulkEmailBody,
                      recipient_emails: selectedUserEmails,
                      from_address: bulkEmailFromAddress,
                      use_template: true,
                    }
                    bulkEmailMutation.mutate(payload)
                  }}
                  disabled={bulkEmailMutation.isPending || selectedUserEmails.length === 0}
                  className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors"
                >
                  {bulkEmailMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <EnvelopeIcon className="w-4 h-4 mr-2" />
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Users table with selection */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              {isLoadingUsers ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                  <p className="mt-4 text-slate-600 dark:text-slate-400">Loading users...</p>
                </div>
              ) : userMetrics && userMetrics.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                    <thead className="bg-slate-50 dark:bg-slate-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            checked={
                              selectedUserEmails.length > 0 &&
                              selectedUserEmails.length === userMetrics.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUserEmails(userMetrics.map((u) => u.email))
                              } else {
                                setSelectedUserEmails([])
                              }
                            }}
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Documents
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Token Usage
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Last Active
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                      {userMetrics.map((user) => {
                        const isSelected = selectedUserEmails.includes(user.email)
                        return (
                          <tr
                            key={user.user_id}
                            className={clsx(
                              'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                              isSelected && 'bg-teal-50/60 dark:bg-teal-900/10'
                            )}
                          >
                            <td className="px-4 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUserEmails((prev) =>
                                      prev.includes(user.email) ? prev : [...prev, user.email]
                                    )
                                  } else {
                                    setSelectedUserEmails((prev) =>
                                      prev.filter((email) => email !== user.email)
                                    )
                                  }
                                }}
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-slate-900 dark:text-white">
                                  {user.username}
                                </div>
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                  {user.email}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-white">
                              {formatNumber(user.document_count)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-white">
                              {formatNumber(user.total_token_usage)} {/* Note: Backend still uses token_usage field name */}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-white">
                              {formatCurrency(user.total_cost_usd)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                              {user.last_active_at
                                ? new Date(user.last_active_at).toLocaleDateString()
                                : 'Never'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="relative">
                                <button
                                  onClick={() => {
                                    setSelectedUserForAction(
                                      selectedUserForAction === user.user_id ? null : user.user_id
                                    )
                                  }}
                                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 focus:outline-none"
                                >
                                  <EllipsisVerticalIcon className="w-5 h-5" />
                                </button>
                                {selectedUserForAction === user.user_id && (
                                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-slate-800 ring-1 ring-black ring-opacity-5 z-10">
                                    <div className="py-1">
                                      <button
                                        onClick={() => {
                                          setSelectedUserEmails((prev) =>
                                            prev.includes(user.email) ? prev : [...prev, user.email]
                                          )
                                          setSelectedUserForAction(null)
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                                      >
                                        <EnvelopeIcon className="w-4 h-4 inline mr-2" />
                                        Send Email
                                      </button>
                                      <button
                                        onClick={() => {
                                          setUserToDelete(user)
                                          setShowDeleteConfirm(true)
                                          setSelectedUserForAction(null)
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                      >
                                        <TrashIcon className="w-4 h-4 inline mr-2" />
                                        Delete User
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-600 dark:text-slate-400">No users found</div>
              )}
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">LaTeX Templates</h2>
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-colors"
              >
                <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
                Upload Template
              </button>
            </div>

            {isLoadingTemplates ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                <p className="mt-4 text-slate-600 dark:text-slate-400">Loading templates...</p>
              </div>
            ) : templates && templates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map((template) => (
                  <div
                    key={template.name}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{template.name}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                          {template.file_count} files
                        </p>
                        {template.has_main_tex && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300 mt-2">
                            Has main.tex
                          </span>
                        )}
                      </div>
                      {template.name !== 'default' && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete template "${template.name}"? This cannot be undone.`)) {
                              deleteTemplateMutation.mutate(template.name)
                            }
                          }}
                          disabled={deleteTemplateMutation.isPending}
                          className="ml-4 p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">No templates found</div>
            )}
          </div>
        )}

        {/* Credit Usage Tab */}
        {activeTab === 'tokens' && (
          <div className="space-y-6">
            {isLoadingTokens ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                <p className="mt-4 text-slate-600 dark:text-slate-400">Loading token usage...</p>
              </div>
            ) : tokenUsage ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Tokens</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                      {formatNumber(tokenUsage.total_tokens)}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Cost</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                      {formatCurrency(tokenUsage.total_cost_usd)}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Operations</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                      {Object.keys(tokenUsage.operation_counts).length}
                    </p>
                  </div>
                </div>

                {/* Operation Breakdown */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Operation Breakdown</h3>
                  <div className="space-y-2">
                    {Object.entries(tokenUsage.operation_counts).map(([operation, count]) => (
                      <div key={operation} className="flex items-center justify-between py-2 border-b border-slate-200 dark:border-slate-700 last:border-0">
                        <span className="text-sm font-medium text-slate-900 dark:text-white capitalize">
                          {operation.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm text-slate-600 dark:text-slate-400">{formatNumber(count)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Users */}
                {tokenUsage.user_breakdown.length > 0 && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-slate-700">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Top Users by Token Usage</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                        <thead className="bg-slate-50 dark:bg-slate-900">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              User ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              Tokens
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              Cost
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                          {tokenUsage.user_breakdown.slice(0, 20).map((user) => (
                            <tr key={user.user_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-900 dark:text-white">
                                {user.user_id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-white">
                                {formatNumber(user.total_tokens)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-white">
                                {formatCurrency(user.total_cost_usd)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">Failed to load token usage</div>
            )}
          </div>
        )}

        {/* Delete User Confirmation Modal */}
        {showDeleteConfirm && userToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Delete User
                </h3>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setUserToDelete(null)
                  }}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
                    ⚠️ Warning: This action cannot be undone!
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Deleting this user will permanently remove:
                  </p>
                  <ul className="text-sm text-red-700 dark:text-red-300 mt-2 ml-4 list-disc">
                    <li>User account: {userToDelete.email}</li>
                    <li>All documents owned by this user ({userToDelete.document_count} documents)</li>
                    <li>LaTeX project files</li>
                    <li>Chat history and messages</li>
                    <li>Token usage records</li>
                    <li>All other user data</li>
                  </ul>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                    <strong>Note:</strong> Documents shared with other users will be kept, but the share will be removed.
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false)
                      setUserToDelete(null)
                    }}
                    className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      deleteUserMutation.mutate(userToDelete.user_id)
                    }}
                    disabled={deleteUserMutation.isPending}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteUserMutation.isPending ? (
                      <>
                        <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <TrashIcon className="w-4 h-4 inline mr-2" />
                        Delete User
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Template Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Upload Template</h3>
                <button
                  onClick={() => {
                    setShowUploadModal(false)
                    setUploadTemplateName('')
                    setUploadFile(null)
                  }}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={uploadTemplateName}
                    onChange={(e) => setUploadTemplateName(e.target.value)}
                    placeholder="e.g., conference-paper"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    ZIP File
                  </label>
                  <input
                    type="file"
                    accept=".zip"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Upload a ZIP file containing your template. Must include a main.tex file.
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowUploadModal(false)
                      setUploadTemplateName('')
                      setUploadFile(null)
                    }}
                    className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={uploadTemplateMutation.isPending || !uploadTemplateName.trim() || !uploadFile}
                    className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:from-teal-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadTemplateMutation.isPending ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminPage

